// Locations list + admin actions (add/edit on a dedicated page; see ./EditForm).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, List, MapPin, Phone } from 'lucide-react';
import { deleteLocation, getLocations, getRegions } from '@/api/locations';
import { getCodeBoard } from '@/api/baseplanCodes';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { useSort, sortRows, matchesQuery } from '@/lib/sortTable';
import { SortHeader } from '@/components/ui/SortHeader';
import { austPostLabels, locationTypeLabels } from './schema';
import type { BaseplanCode, Location } from '@/types/oscar';

type Unlinked = { c: BaseplanCode; areaDesc: string | null; regionId: number | null; regionDesc: string | null };
type Group = { key: string; label: string; locations: Location[]; unlinked: Unlinked[] };

const NO_REGION = 'No region';
const byText = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').localeCompare(b ?? '', undefined, { numeric: true, sensitivity: 'base' });

export function LocationsPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const [type, setType] = useState('');
  const { sortKey, sortDir, onSort } = useSort('name');
  const [deleting, setDeleting] = useState<Location | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('table');
  // Anthony, 25/10/2026: group by region, active by default, a box for the inactive
  // ones, and the reporting branches that have no location yet.
  const [groupByRegion, setGroupByRegion] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showUnlinked, setShowUnlinked] = useState(true);

  const query = useQuery({
    queryKey: ['locations', { search, region }],
    queryFn: ({ signal }) => getLocations({ search: search || undefined, region: region ? Number(region) : undefined, activeOnly: false }, signal),
  });
  const regionsQuery = useQuery({ queryKey: ['locations', 'regions'], queryFn: ({ signal }) => getRegions(signal), retry: false });
  // Same key as the reporting branches board and the edit form, so they share a cache.
  const codesQuery = useQuery({ queryKey: ['locations', 'codes'], queryFn: ({ signal }) => getCodeBoard(signal), retry: false });
  const mDelete = useMutation({
    mutationFn: (id: number) => deleteLocation(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['locations'] }); setDeleting(null); },
  });

  const colValue = (l: Location, k: string): unknown =>
    ({ name: l.LocationDesc, type: l.LocationType, branchCode: l.BranchCode, site: l.ParentLocationDesc, region: l.RegionDesc, area: l.AreaDesc, state: l.StateCode } as Record<string, unknown>)[k];
  const all = query.data ?? [];
  const typeOpts = [...new Set(all.map((l) => l.LocationType).filter(Boolean))].sort();
  const matching = all.filter((l) => matchesQuery(search, [l.LocationDesc, l.AdOffice, l.BranchCode, (l.BranchCodes ?? []).join(' '), l.ParentLocationDesc, l.RegionDesc, l.AreaDesc, l.LocationType, l.StateCode]) && (!type || l.LocationType === type));
  const inactiveCount = matching.filter((l) => !l.Active).length;
  const rows = sortRows(matching.filter((l) => showInactive || l.Active), (l) => colValue(l, sortKey), sortDir);

  // Reporting branches (oscar.BaseplanCode) that no live location holds. Not a
  // location type, so a Type filter hides them.
  const board = codesQuery.data;
  const unlinked: Unlinked[] = !board || !showUnlinked || type ? [] : board.Codes
    .filter((c) => c.Locations === 0 && (showInactive || c.IsActive))
    .map((c) => {
      const a = board.Areas.find((x) => x.AreaId === c.AreaId);
      const r = board.Regions.find((x) => x.RegionId === a?.RegionId);
      return { c, areaDesc: a?.AreaDesc ?? null, regionId: r?.RegionId ?? null, regionDesc: r?.RegionDesc ?? null };
    })
    .filter((u) => (!region || u.regionId === Number(region)) && matchesQuery(search, [u.c.Code, u.c.Description, u.areaDesc, u.regionDesc]))
    .sort((x, y) => byText(x.c.Code, y.c.Code));

  const groups: Group[] = (() => {
    if (!groupByRegion) return [{ key: 'all', label: '', locations: rows, unlinked }];
    const order = new Map((board?.Regions ?? []).map((r) => [r.RegionDesc, r.RegionSort]));
    const map = new Map<string, Group>();
    const get = (label: string | null) => {
      const k = label ?? NO_REGION;
      if (!map.has(k)) map.set(k, { key: k, label: k, locations: [], unlinked: [] });
      return map.get(k)!;
    };
    rows.forEach((l) => get(l.RegionDesc).locations.push(l));
    unlinked.forEach((u) => get(u.regionDesc).unlinked.push(u));
    return [...map.values()].sort((x, y) =>
      (x.key === NO_REGION ? 1 : 0) - (y.key === NO_REGION ? 1 : 0)
      || byText(order.get(x.key) ?? null, order.get(y.key) ?? null) || byText(x.label, y.label));
  })().filter((g) => g.locations.length + g.unlinked.length > 0);
  const nothing = rows.length === 0 && unlinked.length === 0 && !query.isLoading && !query.isError;
  const groupSummary = (g: Group) =>
    `${g.locations.length} location${g.locations.length === 1 ? '' : 's'}`
    + (g.unlinked.length ? ` · ${g.unlinked.length} reporting branch${g.unlinked.length === 1 ? '' : 'es'} with no location` : '');

  return (
    <section>
      <header className="mb-5 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Locations</h1>
          <p className="mt-1 text-sm text-darkGrey/80">Branches and warehouses — Oscar's owned location hierarchy (Region → Area → Location).</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Link to="/locations/hierarchy" className="rounded border border-border bg-white px-3 py-2 text-sm font-semibold text-navy hover:bg-bg">Regions &amp; areas</Link>
            <Link to="/locations/new" className="inline-flex items-center gap-2 rounded bg-orange px-3 py-2 text-sm font-semibold text-white hover:opacity-90">
              <MapPin className="h-4 w-4" aria-hidden /> Add location
            </Link>
          </div>
        )}
      </header>

      <div className="mb-2 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Region
          <select aria-label="Filter by region" value={region} onChange={(e) => setRegion(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {(regionsQuery.data ?? []).map((r) => <option key={r.RegionId} value={r.RegionId}>{r.RegionDesc}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-darkGrey">
          Type
          <select aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value)} className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal">
            <option value="">All</option>
            {typeOpts.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-1 flex-col text-xs font-semibold text-darkGrey">
          Search
          <input aria-label="Search locations" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Location, AD office, site or branch code" className="mt-1 rounded border border-border bg-white px-2 py-1.5 text-sm font-normal" />
        </label>
        <div className="inline-flex rounded border border-border bg-white p-0.5 text-sm" role="group" aria-label="View">
          {([['table', 'Table', List], ['cards', 'Cards', LayoutGrid]] as const).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              aria-pressed={view === id}
              onClick={() => setView(id)}
              className={`inline-flex items-center gap-1 rounded px-2.5 py-1 font-medium ${view === id ? 'bg-navy text-white' : 'text-darkGrey/70 hover:text-navy'}`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden /> {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-darkGrey">
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={groupByRegion} onChange={(e) => setGroupByRegion(e.target.checked)} className="accent-navy" />
          Group by region
        </label>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} className="accent-navy" />
          Show inactive{inactiveCount > 0 ? ` (${inactiveCount})` : ''}
        </label>
        <label className="inline-flex items-center gap-1.5" title="Reporting branches in the hierarchy that no location holds yet">
          <input type="checkbox" checked={showUnlinked} onChange={(e) => setShowUnlinked(e.target.checked)} className="accent-navy" />
          Reporting branches with no location
        </label>
      </div>

      {view === 'cards' && (
        <div>
          {query.isLoading && <p className="py-6 text-center text-sm text-darkGrey/60">Loading…</p>}
          {query.isError && <p className="py-6 text-center text-sm text-red">Could not load locations.</p>}
          {nothing && <p className="py-4 text-center text-sm text-darkGrey/60">No locations match the filters.</p>}
          {groups.map((g) => (
            <section key={g.key} aria-label={g.label || 'Locations'} className="mb-5">
              {groupByRegion && (
                <h2 className="mb-2 flex items-baseline gap-2 border-b border-border pb-1 font-heading text-base font-bold text-navy">
                  {g.label} <span className="text-xs font-normal text-darkGrey/70">{groupSummary(g)}</span>
                </h2>
              )}
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3" aria-label={g.label ? `Locations in ${g.label}` : 'Locations'}>
                {g.locations.map((l) => (
                  <LocationCard key={l.LocationId} l={l} isAdmin={isAdmin} onDelete={() => { mDelete.reset(); setDeleting(l); }} />
                ))}
                {g.unlinked.map((u) => <UnlinkedCard key={u.c.Code} u={u} isAdmin={isAdmin} />)}
              </ul>
            </section>
          ))}
        </div>
      )}

      {view === 'table' && (
        <div className="overflow-x-auto rounded border border-border bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-bg text-left text-xs uppercase tracking-wide text-darkGrey/70">
              <tr>
                <SortHeader label="Location" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="Type" columnKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="Part of site" columnKey="site" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="Branches" columnKey="branchCode" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="Region" columnKey="region" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="Area" columnKey="area" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <SortHeader label="State" columnKey="state" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                <th className="px-3 py-2">Stock?</th>
                <th className="px-3 py-2">Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            {(query.isLoading || query.isError || nothing) && (
              <tbody>
                {query.isLoading && <tr><td colSpan={10} className="px-3 py-6 text-center text-darkGrey/60">Loading…</td></tr>}
                {query.isError && <tr><td colSpan={10} className="px-3 py-6 text-center text-red">Could not load locations.</td></tr>}
                {nothing && <tr><td colSpan={10} className="px-3 py-4 text-center text-darkGrey/60">No locations match the filters.</td></tr>}
              </tbody>
            )}
            {groups.map((g) => (
              <tbody key={g.key}>
                {groupByRegion && (
                  <tr className="border-t-2 border-navy/20 bg-[#F4F6F9]">
                    <th colSpan={10} scope="rowgroup" className="px-3 py-1.5 text-left">
                      <span className="font-heading text-sm font-bold text-navy">{g.label}</span>
                      <span className="ml-2 text-xs font-normal text-darkGrey/70">{groupSummary(g)}</span>
                    </th>
                  </tr>
                )}
                {g.locations.map((l) => (
                  <tr key={l.LocationId} className={`border-t border-border${l.Active ? '' : ' opacity-50'}`}>
                    <td className="px-3 py-2 font-medium text-navy">
                      {isAdmin ? <Link to={`/locations/${l.LocationId}/edit`} className="hover:underline">{l.LocationDesc}</Link> : l.LocationDesc}
                      {l.AdOffice && l.AdOffice !== l.LocationDesc && <span className="block text-xs font-normal text-darkGrey/70" title="AD office">{l.AdOffice}</span>}
                    </td>
                    <td className="px-3 py-2 text-darkGrey/80">{locationTypeLabels[l.LocationType] ?? l.LocationType}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.ParentLocationDesc ?? '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80"><BranchList l={l} /></td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.RegionDesc ?? '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.AreaDesc ?? '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.StateCode ?? '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.IsStockLocation ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{l.Active ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <span className="flex justify-end gap-2">
                          <Link to={`/locations/${l.LocationId}/edit`} className="text-xs font-semibold text-navy hover:underline">Edit</Link>
                          <button type="button" onClick={() => { mDelete.reset(); setDeleting(l); }} className="text-xs font-semibold text-red hover:underline">Delete</button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {g.unlinked.map((u) => (
                  <tr key={`code-${u.c.Code}`} className={`border-t border-dashed border-border${u.c.IsActive ? '' : ' opacity-50'}`}>
                    <td className="px-3 py-2 italic text-darkGrey">{u.c.Description}</td>
                    <td className="px-3 py-2"><span className="rounded border border-dashed border-orange px-1.5 py-0.5 text-xs text-darkGrey">No location</span></td>
                    <td className="px-3 py-2 text-darkGrey/60">—</td>
                    <td className="px-3 py-2 font-mono text-darkGrey/80">{u.c.Code}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{u.regionDesc ?? '—'}</td>
                    <td className="px-3 py-2 text-darkGrey/80">{u.areaDesc ?? <span className="italic text-darkGrey/60">Unallocated</span>}</td>
                    <td className="px-3 py-2 text-darkGrey/60">—</td>
                    <td className="px-3 py-2 text-darkGrey/60">—</td>
                    <td className="px-3 py-2 text-darkGrey/80">{u.c.IsActive ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <Link to={`/locations/new?code=${encodeURIComponent(u.c.Code)}`} aria-label={`Add a location for ${u.c.Code} ${u.c.Description}`} className="text-xs font-semibold text-navy hover:underline">Add location</Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}

      <AlertDialog
        open={deleting !== null}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        title="Delete this location?"
        description={`${deleting?.LocationDesc ?? ''} will be soft-deleted and hidden from the pickers.`}
        confirmLabel="Delete"
        destructive
        closeOnConfirm={false}
        onConfirm={() => { if (deleting) mDelete.mutate(deleting.LocationId); }}
      >
        {mDelete.isError && (
          <p role="alert" className="text-xs text-red">
            {mDelete.error instanceof ApiError ? mDelete.error.message : 'Could not delete this location.'}
          </p>
        )}
      </AlertDialog>
    </section>
  );
}

/** The branches at a location, the primary first (team walk-through 26/10/2026: Pakenham runs two). */
function branchesOf(l: Location): string[] {
  const list = l.BranchCodes ?? [];
  return list.length > 0 ? list : l.BranchCode ? [l.BranchCode] : [];
}

/** The table's Branches cell: the primary, then the others. */
function BranchList({ l }: { l: Location }) {
  const codes = branchesOf(l);
  if (codes.length === 0) return <>—</>;
  const [first, ...rest] = codes;
  return (
    <span>
      <span className={l.BranchCode ? 'font-semibold text-navy' : undefined} title={l.BranchCode ? 'Primary branch' : undefined}>{first}</span>
      {rest.length > 0 && <span className="text-darkGrey/70">, {rest.join(', ')}</span>}
    </span>
  );
}

/** A reporting branch with no location yet - a placeholder card in its region. */
function UnlinkedCard({ u, isAdmin }: { u: Unlinked; isAdmin: boolean }) {
  return (
    <li className={`flex flex-col rounded border border-dashed border-orange/60 bg-white p-3${u.c.IsActive ? '' : ' opacity-60'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-heading text-sm font-bold text-darkGrey">{u.c.Description}</h3>
          <p className="text-xs text-darkGrey/70">Reporting branch · no location yet</p>
        </div>
        <span className="shrink-0 rounded bg-[#E6ECF2] px-1.5 py-0.5 font-mono text-xs font-semibold text-navy">{u.c.Code}</span>
      </div>
      <p className="mt-2 text-xs text-darkGrey">
        {[u.regionDesc, u.areaDesc].filter(Boolean).join(' › ') || <span className="text-darkGrey/60">Unallocated</span>}
      </p>
      {isAdmin && (
        <div className="mt-auto flex justify-end pt-2">
          <Link to={`/locations/new?code=${encodeURIComponent(u.c.Code)}`} aria-label={`Add a location for ${u.c.Code} ${u.c.Description}`} className="text-xs font-semibold text-navy hover:underline">Add location</Link>
        </div>
      )}
    </li>
  );
}

/** One location as a card: what someone looking for a branch needs at a glance. */
function LocationCard({ l, isAdmin, onDelete }: { l: Location; isAdmin: boolean; onDelete: () => void }) {
  const street = l.EffectivePhysicalStreet;
  const place = [l.EffectivePhysicalCity, l.EffectivePhysicalState, l.EffectivePhysicalPostcode].filter(Boolean).join(' ');
  const postal = l.PostOfficeBox ? [l.PostOfficeBox, l.PostalCity, l.PostalState, l.PostalPostcode].filter(Boolean).join(' ') : null;
  return (
    <li className={`flex flex-col rounded border border-border bg-white p-3 shadow-sm${l.Active ? '' : ' opacity-60'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-heading text-sm font-bold text-navy">
            {isAdmin ? <Link to={`/locations/${l.LocationId}/edit`} className="hover:underline">{l.LocationDesc}</Link> : l.LocationDesc}
          </h3>
          <p className="text-xs text-darkGrey/70">
            {locationTypeLabels[l.LocationType] ?? l.LocationType}
            {l.ParentLocationDesc ? ` · part of ${l.ParentLocationDesc}` : ''}
          </p>
        </div>
        {branchesOf(l).length > 0 && (
          <span className="flex shrink-0 flex-wrap justify-end gap-1" aria-label="Branches">
            {branchesOf(l).map((c, i) => (
              <span
                key={c}
                title={i === 0 && l.BranchCode ? 'Primary branch' : 'Branch at this location'}
                className={`rounded px-1.5 py-0.5 font-mono text-xs ${i === 0 && l.BranchCode ? 'bg-[#E6ECF2] font-semibold text-navy' : 'border border-[#E6ECF2] text-darkGrey'}`}
              >
                {c}
              </span>
            ))}
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-darkGrey">
        {[l.RegionDesc, l.AreaDesc].filter(Boolean).join(' › ') || <span className="text-darkGrey/50">No region or area</span>}
      </p>
      <address className="mt-2 text-sm not-italic text-darkGrey">
        {street || place ? (
          <>
            {street && <span className="block">{street}</span>}
            {place && <span className="block">{place}</span>}
            {l.AddressInherited && <span className="block text-xs text-darkGrey/60">Site address</span>}
          </>
        ) : (
          <span className="text-darkGrey/50">No address</span>
        )}
        {postal && <span className="mt-1 block text-xs text-darkGrey/70">Post: {postal}</span>}
      </address>
      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
        {l.Phone && (
          <a href={`tel:${l.Phone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-1 text-navy hover:underline">
            <Phone className="h-3 w-3" aria-hidden /> {l.Phone}
          </a>
        )}
        {l.AustPostDelivers && (
          <span
            className={`rounded px-1.5 py-0.5 font-semibold ${l.AustPostDelivers === 'Yes' ? 'bg-[#E6ECF2] text-navy' : 'bg-orange/10 text-darkGrey'}`}
            title="Does Australia Post deliver to the physical address?"
          >
            Aust Post: {austPostLabels[l.AustPostDelivers]}
          </span>
        )}
        {l.IsStockLocation && <span className="rounded bg-[#F4F6F9] px-1.5 py-0.5 text-darkGrey/80">Stock</span>}
        {!l.Active && <span className="rounded bg-[#F4F6F9] px-1.5 py-0.5 text-darkGrey/80">Inactive</span>}
      </div>
      {l.AddressNotes && <p className="mt-2 line-clamp-2 text-xs text-darkGrey/70" title={l.AddressNotes}>{l.AddressNotes}</p>}
      {isAdmin && (
        <div className="mt-auto flex justify-end gap-3 pt-2">
          <Link to={`/locations/${l.LocationId}/edit`} className="text-xs font-semibold text-navy hover:underline">Edit</Link>
          <button type="button" onClick={onDelete} className="text-xs font-semibold text-red hover:underline">Delete</button>
        </div>
      )}
    </li>
  );
}
